/** SITHS AUTHENTICATION CONTEXT & PROVIDER */

import { createContext, useState, useEffect } from "react";
import { api, setAuth } from "../config";
import { endpoints } from "./consts";

export const AuthenticationContext = createContext();

export const AUTH_MODES = {
  siths: "siths",
  bankid: "bankid",
};
export const AuthenticationProvider = ({ children }) => {
  /** SETUP */
  const [authenticated, setAuthenticated] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [sessionId, setSessionId] = useState("");
  const [hsaId, setHsaId] = useState("");
  const [user, setUser] = useState({});
  const [error, setError] = useState(null);

  const [requestAuthPending, setRequestAuthPending] = useState(false);
  const [sessionPending, setSessionPending] = useState(false);

  /** INTERNAL FUNCTIONS */
  const _init = () => {
    setAuthenticated(false);
    setSessionId("");
    setHsaId("");
    setUser({
      username: "",
      firstName: "",
      lastName: "",
      email: "",
      groups: [],
      staff: null,
      isAdmin: null,
    });
    setError(null);
    setRequestAuthPending(false);
    setSessionPending(false);
    document.cookie = "sithssession=;Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure";
    document.cookie = "bankidsession=;Expires=Thu, 01 Jan 1970 00:00:01 GMT; SameSite=None; Secure";
    setAuth();
  };

  /**
   * Check if auth should refresh itself.
   * - When root is clicked set shouldRefreshAuth to true and reset timeout (5 minutes) until it turns to false
   */
  const [shouldRefreshAuth, setShouldRefreshAuth] = useState(true);

  useEffect(() => {
    const handleOnTimeoutReached = () => {
      setShouldRefreshAuth(false);
    };
    let timeout;
    let interval;
    const handleOnTimeoutReset = () => {
      setShouldRefreshAuth(true);
      clearTimeout(timeout);
      timeout = setTimeout(() => handleOnTimeoutReached(), 1000 * 60 * 20);
    };

    timeout = setTimeout(() => handleOnTimeoutReached(), 1000 * 60 * 20);
    window.addEventListener("blur", handleOnTimeoutReset);
    window.frames.addEventListener("mousemove", handleOnTimeoutReset);
    window.frames.addEventListener("mouseenter", handleOnTimeoutReset);
    window.frames.addEventListener("mouseleave", handleOnTimeoutReset);

    for (const iframe of document.getElementsByTagName("iframe")) {
      iframe.addEventListener("load", handleOnTimeoutReset);
    }
    interval = setInterval(() => {
      if (shouldRefreshAuth) api.get(endpoints.SITHS_CHECK_AUTH);
    }, 1000 * 60 * 5);

    return () => {
      clearTimeout(timeout);
      clearInterval(interval);
      window.removeEventListener("blur", handleOnTimeoutReset);
      window.frames.removeEventListener("mousemove", handleOnTimeoutReset);
      window.frames.removeEventListener("mouseenter", handleOnTimeoutReset);
      window.frames.removeEventListener("mouseleave", handleOnTimeoutReset);
      for (const iframe of document.getElementsByTagName("iframe")) {
        iframe.removeEventListener("load", handleOnTimeoutReset);
      }
    };
  }, [shouldRefreshAuth]);

  /** EXPORTED FUNCTIONS */

  /** Generic function to retrive cookies by key. */
  function getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  /**
   * Returns "cookieKey=cookieValue" of available set authenticated session cookie.
   * Defaults to "" if none are available.
   **/
  function getAuthCookie() {
    for (const mode of Object.keys(AUTH_MODES)) {
      const session = getCookie(mode + "session");
      if (session && session !== null && session !== "") return mode + "session=" + session;
    }
    return "";
  }

  const dispatchError = (message) => {
    setAuthenticated(false);
    setRedirectUrl(null);
    setHsaId("");
    setUser({});
    setError(message);

    setRequestAuthPending(false);
    setSessionPending(false);
  };

  /**
   * Request session until backend has verified identity
   * @param authMode = AUTH_MODE
   **/
  const getSession = (authMode, paramSessionId = null) => {
    // If the user is not authenticated but state has sessionId get session data
    // console.log("Called getSession with \nmode:", authMode, "\nparamSessionId:", paramSessionId);
    if (!authenticated && (sessionId || paramSessionId)) {
      //   console.log("Requesting session from Session ID.");
      setSessionPending(true);
      let _sessionId = sessionId;
      if (paramSessionId) {
        _sessionId = paramSessionId;
        setSessionId(paramSessionId);
      }
      /** SITHS SESSION */
      api
        .get(endpoints.SITHS_SESSION + _sessionId + "/?mode=" + AUTH_MODES[authMode])
        .then((response) => {
          if (response.data.success) {
            setAuth("HSAID " + response.data.data.hsa_id);
            setAuthenticated(true);
            setRedirectUrl(null);
            setHsaId(response.data.data.hsa_id);
            setUser(response.data.data.user);
            setRequestAuthPending(false);
            document.cookie = AUTH_MODES[authMode] + "session=" + _sessionId;
          } else {
            _init();
            // dispatchError("Det gick inte att logga in.");
          }
        })
        .catch((error) => {
          dispatchError("Det gick inte att logga in.");
        })
        .finally(() => setSessionPending(false));
    }
  };

  /** Logout with active token */
  const doLogout = () => {
    if (authenticated) {
      api
        .put(endpoints.SITHS_LOGOUT)
        .then(() => {
          _init();
        })
        .catch((error) => {
          dispatchError(error.data);
        });
    } else {
      dispatchError("Not logged in");
    }
  };

  /** Check permissions.
   *  @param {Object}     permissions
   * { palTypes: [] or '\*', palGroups: [] or '\*', workCategories: [] or '\*', allowAdmin: bool, allowStaff: bool, conditionalFunction: () => bool }
   * @returns {Boolean} isPermitted
   */
  const isPermitted = (permissions) => {
    if (permissions.allowAll) return true; // Overrides other checks

    // singlePermitted
    const allowAdmin = typeof permissions.allowAdmin === "undefined" ? true : permissions.allowAdmin; // (Superuser) Default to true
    const allowStaff = typeof permissions.allowStaff === "undefined" ? false : permissions.allowStaff; // Default to false
    if (allowAdmin && user.is_admin) return true;
    if (allowStaff && user.is_staff) return true;
    const conditionalFunction =
      typeof permissions.conditionalFunction === "undefined" ? true : permissions.conditionalFunction(); // Default to true

    let palGroupsPermitted = permissions.palGroups === "*" ? true : false;
    if (!palGroupsPermitted && permissions.palGroups && user.pal_group) {
      permissions.palGroups.forEach((group) => {
        if (group.toLowerCase() === user.pal_group.toLowerCase()) palGroupsPermitted = true;
      });
    }

    // let specificTypesPermitted = true;
    let palTypesPermitted = permissions.palTypes === "*" ? true : false;

    if (!palTypesPermitted && permissions.palTypes && user.pal_type) {
      permissions.palTypes.forEach((type) => {
        if (type.toLowerCase() === user.pal_type.toLowerCase()) palTypesPermitted = true;
      });
    }

    let wcPermitted = permissions.workCategories === "*" ? true : false;
    if (!wcPermitted && permissions.workCategories && user.person_units) {
      for (const pu of user.person_units) {
        for (const wc of pu.work_categories) {
          if (permissions.workCategories.includes(wc.id) || permissions.workCategories.includes(wc.name)) {
            wcPermitted = true;
          }
        }
      }
    }

    if (permissions.workCategories === "*") {
      return conditionalFunction && palGroupsPermitted && palTypesPermitted;
    } else if (permissions.palGroups === "*" && permissions.palTypes === "*") {
      return conditionalFunction && wcPermitted;
    } else if (permissions.palTypes === "*") {
      return conditionalFunction && (wcPermitted || palGroupsPermitted);
    } else if (permissions.palGroups === "*") {
      return conditionalFunction && (wcPermitted || palTypesPermitted);
    }
    return (conditionalFunction && palGroupsPermitted && palTypesPermitted) || (conditionalFunction && wcPermitted);
  };
  return (
    <AuthenticationContext.Provider
      value={{
        // VARS
        authenticated,
        redirectUrl,
        sessionId,
        hsaId,
        user,
        setUser,
        error,

        requestAuthPending,
        setRequestAuthPending,
        sessionPending,
        setSessionPending,

        // FNCTIONS
        getCookie,
        getAuthCookie,
        getSession,
        doLogout,
        dispatchError,
        isPermitted,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  );
};
